import * as THREE from 'three';
import { show as showPopup, hide as hidePopup } from './popup';
import { getCategoryColor } from './categories';
import type { CoordTransform } from './coords';
import type { PoiData } from './types';

const TIP_HEIGHT  = 1.4;
const HEAD_RADIUS = 0.42;

// Shared geometries — never disposed, live for app lifetime
// Cone base radius matches sphere so they meet flush (classic teardrop pin shape)
const _tipGeo  = new THREE.ConeGeometry(HEAD_RADIUS, TIP_HEIGHT, 12);
const _headGeo = new THREE.SphereGeometry(HEAD_RADIUS, 16, 12);

let currentPins:    THREE.Group[] = [];
let currentMarkers: THREE.Mesh[]  = [];
let raycasterActive = false;
let _poisVisible    = true;
let _activeCategory = 'all';

function buildPin(poi: PoiData): THREE.Group {
    const group = new THREE.Group();
    const color  = getCategoryColor(poi.category);

    const mat = new THREE.MeshStandardMaterial({
        color,
        emissive: color,
        emissiveIntensity: 0.3,
        roughness: 0.25,
        metalness: 0.12,
    });

    // Cone body — base at top (y = TIP_HEIGHT), apex at terrain surface (y = 0)
    const tip = new THREE.Mesh(_tipGeo, mat);
    tip.rotation.x = Math.PI;
    tip.position.y = TIP_HEIGHT / 2;

    // Sphere head sits flush on top of the cone base
    const head = new THREE.Mesh(_headGeo, mat);
    head.position.y = TIP_HEIGHT + HEAD_RADIUS;
    head.userData['poi'] = poi;

    group.add(tip, head);
    group.userData['poiCategory'] = poi.category ?? 'default';

    currentMarkers.push(head);

    return group;
}

export async function loadAndPlacePOIs(
    regionSlug: string,
    coords: CoordTransform,
    scene: THREE.Object3D,
): Promise<THREE.Mesh[]> {
    clearMarkers(scene);

    const url  = regionSlug ? `/api/pois?region=${regionSlug}` : '/api/pois';
    const pois = await fetch(url).then(r => r.json()) as PoiData[];

    for (const poi of pois) {
        const { x, z } = coords.geoToWorld(poi.lon, poi.lat);
        const exagY = coords.sampleTerrainY(poi.lon, poi.lat);
        // Store raw (un-exaggerated) Y so rescaleMarkers stays correct
        const rawY  = coords.exaggeration > 0 ? exagY / coords.exaggeration : exagY;

        const pin = buildPin(poi);
        pin.position.set(x, exagY, z);
        pin.userData['baseY'] = rawY;

        scene.add(pin);         // works for Scene or Group (both extend Object3D)
        currentPins.push(pin);
    }

    applyFilter();
    return currentMarkers;
}

export function rescaleMarkers(exaggeration: number): void {
    for (const pin of currentPins) {
        pin.position.y = (pin.userData['baseY'] as number) * exaggeration;
    }
}

export function clearMarkers(scene: THREE.Object3D): void {
    for (const pin of currentPins) {
        scene.remove(pin);
        pin.traverse(child => {
            if (child instanceof THREE.Mesh || child instanceof THREE.Sprite) {
                // Only dispose per-pin materials; geometries are shared
                if (Array.isArray(child.material)) {
                    child.material.forEach(m => m.dispose());
                } else {
                    child.material.dispose();
                }
            }
        });
    }
    currentPins    = [];
    currentMarkers = [];
}

function applyFilter(): void {
    for (const pin of currentPins) {
        const cat      = pin.userData['poiCategory'] as string;
        const catMatch = _activeCategory === 'all' || cat === _activeCategory;
        pin.visible    = _poisVisible && catMatch;
    }
}

export function setPoisVisible(visible: boolean): void {
    _poisVisible = visible;
    applyFilter();
}

export function filterByCategory(category: string): void {
    _activeCategory = category;
    applyFilter();
}

export function getMarkers(): THREE.Mesh[] {
    return currentMarkers.filter(m => m.parent?.visible ?? false);
}

export function setupInteraction(
    getMarkersFn: () => THREE.Object3D[],
    camera: THREE.Camera,
    renderer: THREE.WebGLRenderer,
): void {
    if (raycasterActive) return;
    raycasterActive = true;

    const raycaster = new THREE.Raycaster();
    const mouse     = new THREE.Vector2();

    function toNDC(e: PointerEvent): void {
        const rect = renderer.domElement.getBoundingClientRect();
        mouse.x =  ((e.clientX - rect.left) / rect.width)  * 2 - 1;
        mouse.y = -((e.clientY - rect.top)  / rect.height) * 2 + 1;
    }

    renderer.domElement.addEventListener('pointermove', (e) => {
        toNDC(e);
        raycaster.setFromCamera(mouse, camera);
        renderer.domElement.style.cursor =
            raycaster.intersectObjects(getMarkersFn()).length ? 'pointer' : '';
    });

    renderer.domElement.addEventListener('click', (e) => {
        toNDC(e);
        raycaster.setFromCamera(mouse, camera);
        const hits = raycaster.intersectObjects(getMarkersFn());
        if (hits.length) {
            showPopup(hits[0].object.userData['poi'] as PoiData);
        } else {
            hidePopup();
        }
    });
}
